/** Jurisio Ask Word add-in client (`app/handlers/scholar_word_addin.py`). */

import type { WordSnapshot } from "../anchor/snapshot";
import { parseWordArtifacts } from "../chat/artifacts";
import { PRODUCTS } from "../config";
import { assertTokenFor, requestJson } from "./http";
import {
  APPLY_REPORT_MAX_ITEMS,
  APPLY_REPORT_REASON_MAX,
  type ApplyReportItem,
  AuthLostError,
  BackendError,
  type ChatBackend,
  type FetchLike,
  type JobActivity,
  type JobCitation,
  type JobCreate,
  type JobEvent,
  type JobPoll,
  type PairStart,
  type PollResult,
  type SaveVersionRequest,
  type SavedVersion,
  type SessionInfo,
  type SnapshotTicket,
  type UploadReport,
  UploadError,
  type WaitOptions,
  type WordBackend,
  WrongProductError,
} from "./types";

interface PairStartWire {
  pair_id: string;
  poll_secret: string;
  user_code: string;
  expires_at: number;
  poll_interval_ms: number;
}

interface PairPollWire {
  status: "pending" | "approved" | "consumed" | "expired" | "denied";
  token?: string;
  expires_at?: number;
  product?: string;
}

interface SessionWire {
  email: string;
  product: string;
}

const MIN_POLL_MS = 2_000;

export function createAskBackend(fetchImpl: FetchLike = (input, init) => fetch(input, init)): WordBackend {
  const config = PRODUCTS.ask;
  const product = config.product;

  const checked = (token: string): string => {
    assertTokenFor(product, token);
    return token;
  };

  return {
    config,

    async startPair(): Promise<PairStart> {
      const wire = await requestJson<PairStartWire>(fetchImpl, config, "/pair/start", { method: "POST", body: {} });
      return {
        pairId: wire.pair_id,
        pollSecret: wire.poll_secret,
        userCode: wire.user_code,
        expiresAt: wire.expires_at,
        pollIntervalMs: Math.max(MIN_POLL_MS, wire.poll_interval_ms),
      };
    },

    async pollPair(pairId: string, pollSecret: string): Promise<PollResult> {
      let wire: PairPollWire;
      try {
        // The secret travels in the POST body, never the query string (spec §6 step 4).
        wire = await requestJson<PairPollWire>(fetchImpl, config, "/pair/poll", {
          method: "POST",
          body: { pair_id: pairId, poll_secret: pollSecret },
        });
      } catch (error) {
        if (error instanceof BackendError && error.status === 429) return { status: "slow_down", retryAfterMs: error.retryAfterMs ?? MIN_POLL_MS };
        if (error instanceof BackendError && error.status === 404) return { status: "expired" };
        throw error;
      }
      if (wire.status !== "approved") return { status: wire.status };
      if (!wire.token) throw new BackendError("The server approved the sign-in but sent no token.", 502);
      if (wire.product !== product) throw new WrongProductError("The server answered for a different Jurisio product.");
      return { status: "approved", token: checked(wire.token), expiresAt: wire.expires_at ?? null };
    },

    async getSession(token: string): Promise<SessionInfo> {
      const wire = await requestJson<SessionWire>(fetchImpl, config, "/session", { method: "GET", token: checked(token) });
      if (wire.product !== product) throw new WrongProductError("This session belongs to a different Jurisio product.");
      return { email: wire.email, product };
    },

    async activity(token: string): Promise<void> {
      await requestJson<void>(fetchImpl, config, "/activity", { method: "POST", token: checked(token) });
    },

    async revoke(token: string): Promise<void> {
      await requestJson<void>(fetchImpl, config, "/revoke", { method: "POST", token: checked(token) });
    },
  };
}

// ---- Word turns: snapshot upload + chat jobs (spec §2.1, §3.1, §7.1) -----------------------------------------

interface SnapshotInitWire {
  snapshot_id: string;
  presigned_post: { url: string; fields: Record<string, string> };
  max_bytes: number;
  expires_in: number;
}

export const TERMINAL_JOB_STATUSES: ReadonlySet<string> = new Set(["completed", "degraded", "failed", "expired"]);
const MIN_JOB_POLL_MS = 400;
const MAX_JOB_POLL_MS = 5_000;
const DEFAULT_JOB_POLL_MS = 1_000;
const MAX_TRANSIENT_POLL_FAILURES = 6;
/** Same order of magnitude as the web app's wait; a Word turn that runs longer is reported, not abandoned silently. */
export const JOB_MAX_WAIT_MS = 15 * 60 * 1000;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

function parseActivity(raw: unknown): JobActivity[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((a): JobActivity[] => {
    if (!isObj(a) || !str(a.kind)) return [];
    const item: JobActivity = { id: str(a.id), kind: str(a.kind), outcome: str(a.outcome) };
    if (str(a.subject)) item.subject = str(a.subject);
    if (typeof a.count === "number" && Number.isFinite(a.count)) item.count = a.count;
    if (str(a.note)) item.note = str(a.note);
    return [item];
  });
}

function parseEvents(raw: unknown): JobEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((e): JobEvent[] => {
    if (!isObj(e)) return [];
    const item: JobEvent = { type: str(e.type), message: str(e.message) };
    if (str(e.stage)) item.stage = str(e.stage);
    return [item];
  });
}

function parseCitations(raw: unknown): JobCitation[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c): JobCitation[] => {
    if (!isObj(c)) return [];
    const num = typeof c.num === "number" && Number.isInteger(c.num) ? c.num : null;
    const url = str(c.url);
    return [{ num, title: str(c.official_title) || str(c.title), url: /^https:\/\//i.test(url) ? url : null, court: strOrNull(c.court) }];
  });
}

function textField(v: unknown): string | null {
  if (typeof v === "string") return v || null;
  if (isObj(v) && typeof v.text === "string") return v.text || null;
  return null;
}

/** Normalise a `GET /v1/scholar/chat/jobs/{id}` (or create) payload. Unknown or malformed fields become empty. */
export function parseJobPoll(raw: unknown): JobPoll {
  if (!isObj(raw) || !str(raw.request_id)) throw new BackendError("Jurisio sent an unreadable reply.", 502);
  const status = str(raw.status) || "queued";
  const pollAfter = typeof raw.poll_after_ms === "number" && Number.isFinite(raw.poll_after_ms) ? raw.poll_after_ms : 0;
  return {
    requestId: str(raw.request_id),
    threadId: strOrNull(raw.thread_id),
    status,
    stage: str(raw.stage),
    terminal: TERMINAL_JOB_STATUSES.has(status),
    pollAfterMs: pollAfter,
    events: parseEvents(raw.events),
    activity: parseActivity(raw.activity),
    thought: textField(raw.thought),
    draftAnswer: textField(raw.draft_answer),
    answer: strOrNull(raw.answer),
    citations: parseCitations(raw.citations),
    wordArtifacts: parseWordArtifacts(raw.word_artifacts),
    errorCode: strOrNull(raw.error_code),
    errorMessage: strOrNull(raw.error_message),
    degradationReasons: Array.isArray(raw.degradation_reasons) ? raw.degradation_reasons.filter((r): r is string => typeof r === "string") : [],
  };
}

/** gzip JSON with the platform's `CompressionStream` (Edge WebView2, Safari ≥ 16.4, Chromium, Node ≥ 18). */
export async function gzipJson(value: unknown): Promise<{ blob: Blob; rawBytes: number }> {
  if (typeof CompressionStream === "undefined") {
    throw new UploadError("This version of Word cannot compress the document for upload. Update Word (or its web browser) and try again.");
  }
  const raw = new TextEncoder().encode(JSON.stringify(value));
  const stream = new Blob([raw]).stream().pipeThrough(new CompressionStream("gzip") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  const gz = await new Response(stream).arrayBuffer();
  return { blob: new Blob([gz], { type: "application/gzip" }), rawBytes: raw.byteLength };
}

interface VersionWire {
  version_id: string;
  name: string;
  doc_key: string;
  thread_id: string;
  created_at: string;
  doc_hash: string;
  char_count: number;
  paragraph_count: number;
  ella_comment_ids: unknown;
}

/** A version row from the wire, or null when it is malformed (dropped, never guessed at). */
export function parseVersion(raw: unknown): SavedVersion | null {
  if (!isObj(raw)) return null;
  const w = raw as unknown as VersionWire;
  if (!/^[0-9a-f]{32}$/.test(str(w.version_id)) || !str(w.name) || !str(w.thread_id)) return null;
  return {
    versionId: w.version_id,
    name: w.name,
    docKey: str(w.doc_key),
    threadId: w.thread_id,
    createdAt: str(w.created_at),
    docHash: str(w.doc_hash),
    charCount: typeof w.char_count === "number" ? w.char_count : 0,
    paragraphCount: typeof w.paragraph_count === "number" ? w.paragraph_count : 0,
    ellaCommentIds: Array.isArray(w.ella_comment_ids) ? w.ella_comment_ids.filter((id): id is string => typeof id === "string" && id.length > 0) : [],
  };
}

/** The report as the server accepts it: at most 60 items, reasons cut to 200 characters. */
export function boundedApplyReport(items: readonly ApplyReportItem[]): ApplyReportItem[] {
  return items.slice(-APPLY_REPORT_MAX_ITEMS).map((item) => {
    const out: ApplyReportItem = { ...item };
    const reason = item.reason?.replace(/\s+/g, " ").trim();
    if (reason) out.reason = reason.length <= APPLY_REPORT_REASON_MAX ? reason : `${reason.slice(0, APPLY_REPORT_REASON_MAX - 1)}…`;
    else delete out.reason;
    return out;
  });
}

function s3ErrorCode(text: string): string {
  return /<Code>([^<]{1,64})<\/Code>/.exec(text)?.[1] ?? "";
}

function rethrowAuth(error: unknown): never {
  if (error instanceof BackendError && error.status === 401) throw new AuthLostError();
  throw error;
}

export function createAskChatBackend(fetchImpl: FetchLike = (input, init) => fetch(input, init), clock: () => number = () => performance.now()): ChatBackend {
  const config = PRODUCTS.ask;
  const checked = (token: string): string => {
    assertTokenFor(config.product, token);
    return token;
  };

  async function pollJob(token: string, requestId: string): Promise<JobPoll> {
    try {
      const wire = await requestJson<unknown>(fetchImpl, config, `/${encodeURIComponent(requestId)}`, {
        method: "GET",
        token: checked(token),
        prefix: config.chatJobsPath,
      });
      return parseJobPoll(wire);
    } catch (error) {
      return rethrowAuth(error);
    }
  }

  return {
    config,

    async initSnapshot(token: string): Promise<SnapshotTicket> {
      let wire: SnapshotInitWire;
      try {
        wire = await requestJson<SnapshotInitWire>(fetchImpl, config, "/snapshots/init", { method: "POST", token: checked(token) });
      } catch (error) {
        return rethrowAuth(error);
      }
      if (!wire?.snapshot_id || !wire.presigned_post?.url || !isObj(wire.presigned_post.fields)) {
        throw new BackendError("Jurisio sent an unreadable upload ticket.", 502);
      }
      const url = wire.presigned_post.url;
      if (!/^https:\/\//i.test(url)) throw new BackendError("Jurisio sent an upload address that is not secure.", 502);
      return { snapshotId: wire.snapshot_id, url, fields: { ...wire.presigned_post.fields }, maxBytes: wire.max_bytes, expiresIn: wire.expires_in };
    },

    async uploadSnapshot(ticket: SnapshotTicket, snapshot: WordSnapshot): Promise<UploadReport> {
      if (snapshot.snapshot_id !== ticket.snapshotId) throw new UploadError("The document snapshot does not match its upload ticket.");
      const started = clock();
      const { blob, rawBytes } = await gzipJson(snapshot);
      if (ticket.maxBytes > 0 && blob.size > ticket.maxBytes) {
        throw new UploadError("This document is too large for Ella to read, even compressed.");
      }
      const form = new FormData();
      // S3 reads the policy fields first; the file must be the LAST part.
      for (const [name, value] of Object.entries(ticket.fields)) form.append(name, value);
      form.append("file", blob, `${ticket.snapshotId}.json.gz`);
      let response: Response;
      try {
        // No Authorization header: the presigned policy is the only credential S3 sees.
        response = await fetchImpl(ticket.url, {
          method: "POST",
          body: form,
          credentials: "omit",
          cache: "no-store",
          mode: "cors",
          redirect: "error",
          referrerPolicy: "no-referrer",
        });
      } catch {
        throw new UploadError("Couldn't upload the document. Check your connection and try again.");
      }
      if (!response.ok) {
        const code = s3ErrorCode(await response.text().catch(() => ""));
        if (code === "EntityTooLarge") throw new UploadError("This document is too large for Ella to read, even compressed.");
        if (code === "AccessDenied" || code === "ExpiredToken" || code === "RequestTimeTooSkewed") {
          throw new UploadError("The secure upload window closed before the document finished uploading. Try again.");
        }
        throw new UploadError(`Couldn't upload the document (storage answered ${response.status}). Try again.`);
      }
      return { rawBytes, gzipBytes: blob.size, ms: Math.round(clock() - started) };
    },

    async createJob(token: string, request: JobCreate): Promise<JobPoll> {
      const body: JobCreate = { message: request.message, word: { snapshot_id: request.word.snapshot_id } };
      const report = request.word.last_apply_report;
      if (report && report.length > 0) body.word.last_apply_report = boundedApplyReport(report);
      if (request.thread_id) body.thread_id = request.thread_id;
      try {
        const wire = await requestJson<unknown>(fetchImpl, config, "", { method: "POST", token: checked(token), body, prefix: config.chatJobsPath });
        return parseJobPoll(wire);
      } catch (error) {
        return rethrowAuth(error);
      }
    },

    pollJob,

    async listVersions(token: string, docKey: string, threadId?: string): Promise<SavedVersion[]> {
      const query = new URLSearchParams({ doc_key: docKey });
      if (threadId) query.set("thread_id", threadId);
      try {
        const wire = await requestJson<{ versions?: unknown }>(fetchImpl, config, `/versions?${query.toString()}`, { method: "GET", token: checked(token) });
        const rows = Array.isArray(wire?.versions) ? wire.versions : [];
        return rows.flatMap((row) => {
          const parsed = parseVersion(row);
          return parsed ? [parsed] : [];
        });
      } catch (error) {
        return rethrowAuth(error);
      }
    },

    async saveVersion(token: string, request: SaveVersionRequest): Promise<SavedVersion> {
      let wire: unknown;
      try {
        wire = await requestJson<unknown>(fetchImpl, config, "/versions", {
          method: "POST",
          token: checked(token),
          body: { snapshot_id: request.snapshotId, doc_key: request.docKey, thread_id: request.threadId, name: request.name },
        });
      } catch (error) {
        return rethrowAuth(error);
      }
      const parsed = parseVersion(wire);
      if (parsed === null) throw new BackendError("Jurisio sent an unreadable version.", 502);
      return parsed;
    },

    async deleteVersion(token: string, versionId: string): Promise<void> {
      try {
        await requestJson<void>(fetchImpl, config, `/versions/${encodeURIComponent(versionId)}`, { method: "DELETE", token: checked(token) });
      } catch (error) {
        return rethrowAuth(error);
      }
    },

    async updateEllaComments(token: string, versionId: string, add: readonly string[], remove: readonly string[]): Promise<SavedVersion> {
      let wire: unknown;
      try {
        wire = await requestJson<unknown>(fetchImpl, config, `/versions/${encodeURIComponent(versionId)}/ella-comments`, {
          method: "POST",
          token: checked(token),
          body: { comment_ids: [...add], removed_ids: [...remove] },
        });
      } catch (error) {
        return rethrowAuth(error);
      }
      const parsed = parseVersion(wire);
      if (parsed === null) throw new BackendError("Jurisio sent an unreadable version.", 502);
      return parsed;
    },

    async waitForJob(token: string, first: JobPoll, options: WaitOptions = {}): Promise<JobPoll> {
      const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
      const now = options.now ?? (() => Date.now());
      const deadline = now() + (options.maxWaitMs ?? JOB_MAX_WAIT_MS);
      let current = first;
      let delay = first.pollAfterMs || DEFAULT_JOB_POLL_MS;
      let failures = 0;
      options.onUpdate?.(current);
      while (!current.terminal) {
        if (now() >= deadline) {
          // It may have finished during the last interval: one last look before reporting a timeout.
          const last = await pollJob(token, current.requestId).catch((error: unknown) => {
            if (error instanceof AuthLostError) throw error;
            return null;
          });
          if (last?.terminal) {
            current = last;
            options.onUpdate?.(current);
            break;
          }
          throw new BackendError("Ella is taking much longer than usual. Try again in a few minutes.", 504);
        }
        await sleep(Math.min(MAX_JOB_POLL_MS, Math.max(MIN_JOB_POLL_MS, delay)));
        try {
          current = await pollJob(token, current.requestId);
        } catch (error) {
          if (error instanceof AuthLostError) throw error;
          const transient =
            error instanceof TypeError || (error instanceof BackendError && (error.status === 429 || error.status >= 500));
          if (!transient || ++failures > MAX_TRANSIENT_POLL_FAILURES) throw error;
          const retryAfter = error instanceof BackendError ? error.retryAfterMs : null;
          delay = retryAfter ?? Math.min(MAX_JOB_POLL_MS, DEFAULT_JOB_POLL_MS * 2 ** (failures - 1));
          continue;
        }
        failures = 0;
        delay = current.pollAfterMs || Math.min(MAX_JOB_POLL_MS, delay * 1.5);
        options.onUpdate?.(current);
      }
      return current;
    },
  };
}
